// Copyright (c) Microsoft. All rights reserved.

import {
    Button,
    createTableColumn,
    DataGrid,
    DataGridBody,
    DataGridCell,
    DataGridHeader,
    DataGridHeaderCell,
    DataGridProps,
    DataGridRow,
    makeStyles,
    Popover,
    PopoverSurface,
    PopoverTrigger,
    shorthands,
    Spinner,
    TableCellLayout,
    TableColumnDefinition,
    tokens,
    Tooltip,
} from '@fluentui/react-components';
import {
    ArrowSyncRegular,
    DeleteRegular,
    DocumentArrowUp20Regular,
    DocumentPdfRegular,
    DocumentTextRegular,
    FluentIconsProps,
} from '@fluentui/react-icons';
import * as React from 'react';
import { useRef } from 'react';
import { Constants } from '../../Constants';
import { useFile } from '../../libs/hooks';
import { Document } from '../../libs/services/DocumentImportService';
import { SharedStyles } from '../../styles';
import { Alerts } from '../shared/Alerts';

export interface Item {
    id: string;
    fileName: string;
    lastUpdated: number;
    partitionCount: number;
}

const useClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        backgroundColor: tokens.colorNeutralBackground1,
    },
    header: {
        ...shorthands.borderBottom('1px', 'solid', 'rgb(0 0 0 / 10%)'),
        ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
        backgroundColor: tokens.colorNeutralBackground1,
        display: 'flex',
        flexDirection: 'row',
        boxSizing: 'border-box',
        width: '100%',
        justifyContent: 'space-between',
    },
    title: {
        ...shorthands.gap(tokens.spacingHorizontalM),
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'row',
        fontWeight: tokens.fontWeightSemibold,
    },
    controls: {
        display: 'flex',
        alignItems: 'center',
        ...shorthands.gap('10px'),
    },
    alerts: {
        ...shorthands.margin('4px'),
    },
    scroll: {
        display: 'flex',
        flexWrap: 'wrap',
        ...shorthands.gap('12px'),
        ...shorthands.margin(tokens.spacingVerticalXS),
        ...shorthands.padding(tokens.spacingVerticalS),
        ...SharedStyles.scroll,
    },
    table: {
        backgroundColor: tokens.colorNeutralBackground1,
        width: '100%',
    },
    linkSpan: {
        cursor: 'pointer',
    },
    popover: {
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap('12px'),
    },
});

export const DocumentsWindow: React.FC = () => {
    const classes = useClasses();
    const fileHandler = useFile();

    const [isImporting, setIsImporting] = React.useState<boolean>(false);
    const [isReloading, setIsReloading] = React.useState<boolean>(false);
    const [isDeleting, setIsDeleting] = React.useState<boolean>(false);

    const [documents, setDocuments] = React.useState<Item[]>([]);
    const globalDocumentFileRef = useRef<HTMLInputElement | null>(null);

    const deleteDocumentsClickHandler = () => {
        setIsDeleting(true);
        void fileHandler
            .deleteDocuments(selectedItems)
            .then(() => {
                // remove from list
                setDocuments(
                    documents.filter((res) => {
                        return !selectedItems.includes(res.id);
                    }),
                );
                setSelectedItems([]);
            })
            .finally(() => {
                setIsDeleting(false);
            });
    };

    const updateList = () => {
        setSelectedItems([]);
        setIsReloading(true);
        void fileHandler
            .listDocuments()
            .then((documents) => {
                setDocuments(
                    documents.map((doc) => {
                        return {
                            ...doc,
                            lastUpdated: Date.parse(doc.lastUpdated),
                        };
                    }),
                );
            })
            .finally(() => {
                setIsReloading(false);
            });
    };

    const openDocumentClickHandler = (fileName: string, documentId: string) => {
        fileHandler.openDocument(documentId, fileName).catch(console.log);
    };

    React.useEffect(() => {
        updateList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const columns: Array<TableColumnDefinition<Item>> = [
        createTableColumn<Item>({
            columnId: 'name',
            renderHeaderCell: () => {
                return 'Name';
            },
            renderCell: (item) => (
                <TableCellLayout media={getFileIconByFileExtension(item.fileName)} truncate>
                    <span
                        className={classes.linkSpan}
                        onClick={(event) => {
                            event.stopPropagation();
                            openDocumentClickHandler(item.fileName, item.id);
                        }}
                    >
                        {item.fileName}
                    </span>
                </TableCellLayout>
            ),
            compare: (a, b) => {
                return a.fileName.localeCompare(b.fileName);
            },
        }),
        createTableColumn<Item>({
            columnId: 'createdOn',
            renderHeaderCell: () => {
                return 'Importiert am';
            },
            renderCell: (item) => {
                return item.id.startsWith('in-progress') ? 'N/A' : formatDateTimeToLocal(item.lastUpdated);
            },
            compare: (a, b) => {
                return a.lastUpdated > b.lastUpdated ? 1 : -1;
            },
        }),
        createTableColumn<Item>({
            columnId: 'partitionCount',
            renderHeaderCell: () => {
                return 'Partitionen';
            },
            renderCell: (item) => {
                return item.id.startsWith('in-progress') ? 'N/A' : item.partitionCount;
            },
        }),
    ];

    const [sortState, setSortState] = React.useState<Parameters<NonNullable<DataGridProps['onSortChange']>>[1]>({
        sortColumn: 'createdOn',
        sortDirection: 'descending',
    });
    const onSortChange: DataGridProps['onSortChange'] = (_, nextSortState) => {
        setSortState(nextSortState);
    };

    const [selectedItems, setSelectedItems] = React.useState<string[]>([]);
    const onSelectionChange: DataGridProps['onSelectionChange'] = (_, data) => {
        setSelectedItems([...data.selectedItems] as string[]);
    };

    const [showDeletePopover, setShowDeletePopover] = React.useState<boolean>(false);

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <div className={classes.title}>Dokumente ({documents.length})</div>
                <div className={classes.controls}>
                    {/* Hidden input for file upload. Only accept .txt and .pdf files for now. */}
                    <input
                        type="file"
                        ref={globalDocumentFileRef}
                        style={{ display: 'none' }}
                        accept={Constants.app.importTypes}
                        multiple={true}
                        onChange={() => {
                            setIsImporting(true);
                            fileHandler
                                .handleDocumentImport(globalDocumentFileRef)
                                .then(() => {
                                    setIsImporting(false);
                                    setTimeout(updateList, 1000);
                                })
                                .catch(console.log);
                        }}
                    />
                    {isImporting && <Spinner size="extra-tiny" label="Import in Arbeit..."></Spinner>}
                    {isReloading && <Spinner size="extra-tiny" label="Aktualisiere Liste..."></Spinner>}
                    {isDeleting && <Spinner size="extra-tiny" label="Löschen in Arbeit..."></Spinner>}
                    <Button
                        appearance="primary"
                        icon={<DocumentArrowUp20Regular />}
                        onClick={() => globalDocumentFileRef.current?.click()}
                        disabled={isImporting}
                    >
                        Hochladen
                    </Button>
                    <Tooltip content="Liste aktualisieren" relationship="label" withArrow>
                        <Button
                            icon={<ArrowSyncRegular />}
                            onClick={() => {
                                updateList();
                            }}
                            disabled={isReloading}
                        />
                    </Tooltip>
                    <Popover
                        positioning="below-end"
                        withArrow
                        open={showDeletePopover}
                        onOpenChange={(_, data) => {
                            setShowDeletePopover(data.open || false);
                        }}
                    >
                        <PopoverTrigger disableButtonEnhancement>
                            <Button icon={<DeleteRegular />} disabled={isDeleting || selectedItems.length === 0}>
                                Markierte löschen
                            </Button>
                        </PopoverTrigger>

                        <PopoverSurface className={classes.popover}>
                            <div>Markierte Dokumente wirklich löschen?</div>
                            <Button
                                onClick={() => {
                                    setShowDeletePopover(false);
                                    deleteDocumentsClickHandler();
                                }}
                            >
                                Ja
                            </Button>
                        </PopoverSurface>
                    </Popover>
                </div>
            </div>
            <div className={classes.alerts}>
                <Alerts />
            </div>
            <div className={classes.scroll}>
                <DataGrid
                    items={documents}
                    columns={columns}
                    sortable
                    sortState={sortState}
                    onSortChange={onSortChange}
                    selectionMode="multiselect"
                    selectionAppearance="neutral"
                    selectedItems={selectedItems}
                    getRowId={(item: Document) => item.id}
                    onSelectionChange={onSelectionChange}
                    className={classes.table}
                >
                    <DataGridHeader>
                        <DataGridRow
                            selectionCell={{
                                checkboxIndicator: { 'aria-label': 'Select all rows' },
                            }}
                        >
                            {({ renderHeaderCell }) => <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>}
                        </DataGridRow>
                    </DataGridHeader>
                    <DataGridBody<Document>>
                        {({ item, rowId }) => (
                            <DataGridRow<Document>
                                key={rowId}
                                selectionCell={{
                                    checkboxIndicator: { 'aria-label': 'Select row' },
                                }}
                            >
                                {({ renderCell }) => <DataGridCell>{renderCell(item)}</DataGridCell>}
                            </DataGridRow>
                        )}
                    </DataGridBody>
                </DataGrid>
            </div>
        </div>
    );
};

function formatDateTimeToLocal(timestamp: number): string {
    const date = new Date(timestamp);

    return date.toLocaleString([], {});
}

export function getFileIconByFileExtension(fileName: string, props: FluentIconsProps = {}) {
    const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.') + 1);
    if (extension === 'pdf') {
        return <DocumentPdfRegular {...props} />;
    }
    return <DocumentTextRegular {...props} />;
}
